import Icon, { IconType } from 'components/Icon';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import {
	Attachment,
	ChatEntry,
	ChatEntryRestriction,
	ChatStatus,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDsChat, selectActiveDesignSystem } from 'redux/db/reducers';
import { showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { onFileDropped } from 'utils';
import { useTheme } from 'styled-components';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import { useChatScroll } from '../../hooks/useChatScroll';
import DsChatInput from '../../ChatInput/DsChatInput';
import DsUpdateCard from './DsUpdateCard';
import {
	splitMessageWithDsUpdates,
	ChatMessageDsUpdatePart,
	ChatMessageTextPart,
} from 'redux/db/util';
import * as Base from '../styles';

const DsConversation = () => {
	const containerRef = useRef<HTMLDivElement>(null);

	// spacer div we will use to force user's last input to scroll to the top of their screen when they submit a prompt
	const spacerRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();
	const theme = useTheme();
	const [isDraggingFile, setIsDraggingFile] = useState(false);

	const { activeDsChat, designSystem, isMobile } = useSelector((r: RootReducer) => {
		const dsChat = selectActiveDsChat(r.db, r.mode.activeDsChatId);
		return {
			activeDsChat: dsChat,
			designSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const lastChatMessage =
		activeDsChat?.entries && activeDsChat.entries.length > 0
			? activeDsChat.entries[activeDsChat.entries.length - 1].message
			: null;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Use shared scroll hook
	const { isScrolledToBottom, onJumpToBottom } = useChatScroll({
		containerRef,
		spacerRef,
		entries: activeDsChat?.entries || [],
		activeChatId: activeDsChat?.id || null,
		lastChatMessage,
		shouldSkipAutoScroll: () => {
			// Only skip if designSystem metadata is not loaded
			return !designSystem?.metadata;
		},
		isInitialized: !!designSystem?.metadata,
	});

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'ds',
		chatId: activeDsChat?.id || null,
	});

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);
		if (!activeDsChat) return;
		onFileDropped(fileCallbacks)(e);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderAttachments = (attachments: Attachment[] | undefined) => {
		if (!attachments?.length) return null;
		return (
			<Base.Attachments
				onWheel={(e: React.WheelEvent) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{attachments.map((attachment) => (
					<ChatAttachmentTile
						key={attachment.storagePath}
						attachment={attachment}
						state={ChatAttachmentTileState.REGULAR}
					/>
				))}
			</Base.Attachments>
		);
	};

	const renderEntries = (entries: ChatEntry[]) => {
		const isLastEntry = (index: number) => index === entries.length - 1;
		const isChatProcessing =
			activeDsChat?.metadata?.status === ChatStatus.PROCESSING ||
			activeDsChat?.metadata?.status === ChatStatus.STOPPABLE;

		return entries.map((entry, index) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				const parts = splitMessageWithDsUpdates(entry.message);
				const showPendingNote = isLastEntry(index) && isChatProcessing;
				return (
					<Base.SystemMessage
						key={index}
						className="system-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{parts.map((part, i) => {
							if (part.type === 'text') {
								const textPart = part as ChatMessageTextPart;
								return (
									<MarkdownText key={i} onContextMenu={onContextMenuInMessage}>
										{textPart.content}
									</MarkdownText>
								);
							} else {
								const updatePart = part as ChatMessageDsUpdatePart;
								return (
									<DsUpdateCard
										key={i}
										updateType={updatePart.updateType}
										isMobile={isMobile}
										showPendingNote={showPendingNote}
									/>
								);
							}
						})}
					</Base.SystemMessage>
				);
			}
			if (entry.kind === 'user') {
				const userEntry = entry as UserChatEntry;
				return (
					<Base.UserMessage
						key={index + 'user'}
						className="user-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{renderAttachments(userEntry.attachments)}
						<MarkdownText onContextMenu={onContextMenuInMessage}>{entry.message}</MarkdownText>
					</Base.UserMessage>
				);
			}
		});
	};

	const renderStatus = () => {
		if (!activeDsChat?.metadata?.statusMessage) return null;
		if (!activeDsChat?.metadata?.status) return null;

		const { status, statusMessage } = activeDsChat.metadata;

		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<Base.StatusMessage isMobile={isMobile}>
				<Base.StatusText>{statusMessage}</Base.StatusText>
				<Loader width={16} height={16} />
			</Base.StatusMessage>
		);
	};

	if (!activeDsChat) {
		return null;
	}

	return (
		<>
			<Base.Base onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
				<Base.ScrollingArea ref={containerRef}>
					{renderEntries(activeDsChat.entries)}
					{renderStatus()}
					<div ref={spacerRef} />
				</Base.ScrollingArea>
				{isDraggingFile && (
					<Base.DragOverlay>
						<Base.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<Base.DragOverlayText isMobile={isMobile}>Drop files to attach</Base.DragOverlayText>
						</Base.DragOverlayContent>
					</Base.DragOverlay>
				)}
				{activeDsChat.entries.length > 0 && <Base.Spacer />}
				<Base.InputArea>
					{!isScrolledToBottom && (
						<Base.JumpButton onClick={onJumpToBottom} isMobile={isMobile}>
							<Base.JumpButtonIcon>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={isMobile ? 24 : 18}
									height={isMobile ? 24 : 18}
									color={theme.gray[700]}
								/>
							</Base.JumpButtonIcon>
						</Base.JumpButton>
					)}
					<DsChatInput key={activeDsChat.id} dsChat={activeDsChat} />
				</Base.InputArea>
			</Base.Base>
		</>
	);
};

export default DsConversation;
